import axios from "axios";
import API_URLS from "../../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Please check the highlighted fields.",
  NOT_FOUND: "Question not found.",
  DUPLICATE_KEY: "A question with that key already exists for this store, or an option key is duplicated.",
  DUPLICATE_STEP: "This step number is already used by another question in this store. Each question must have a unique step.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  const method = (err?.config?.method || "").toUpperCase();
  const url = err?.config?.url || "";

  const fallback =
    apiErr?.message ||
    (status ? `Request failed (${status})` : err?.message || ERROR_MESSAGES.SERVER_ERROR);

  return {
    code: code || (status ? `HTTP_${status}` : "NETWORK_ERROR"),
    status,
    method,
    url,
    message: ERROR_MESSAGES[code] || fallback,
    details: apiErr?.details || [],
  };
};

export const slugify = (text) =>
  String(text || "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

const fetchQuestions = async (filters) => {
  const page = filters.page || 1;
  const limit = filters.limit || 20;

  const params = { page, limit };
  if (filters.store_label) params.store_label = filters.store_label;
  if (filters.store_hash)  params.store_hash  = filters.store_hash;
  if (filters.search)      params.search      = filters.search;
  if (filters.tag_filter && filters.tag_filter !== "all") params.tag_filter = filters.tag_filter;
  if (filters.has_tag) params.has_tag = filters.has_tag;

  const res = await axios.get(API_URLS.GET_QUESTIONS, { headers: headers(), params });
  const payload = res?.data?.data || {};
  return {
    items: payload.items || [],
    pagination: payload.pagination || { page, limit, total: 0, totalPages: 0 },
  };
};



const fetchMaxStep = async (storeLabel) => {
  try {
    const res = await axios.get(API_URLS.QUESTION_MAX_STEP, {
      headers: headers(),
      params: { store_label: storeLabel },
    });
    const max = Number(res?.data?.data?.maxStep ?? 0);
    console.log("[fetchMaxStep] dedicated endpoint =>", max);
    return max;
  } catch (e) {
    console.warn("[fetchMaxStep] dedicated endpoint failed =>", e?.response?.status, e?.message);
    const res = await axios.get(API_URLS.GET_QUESTIONS, {
      headers: headers(),
      params: { store_label: storeLabel, page: 1, limit: 100 },
    });
    const items = res?.data?.data?.items || [];
    console.log("[fetchMaxStep] fallback items:", items.length, "first item keys:", items[0] ? Object.keys(items[0]) : "none", "steps:", items.map(q => q.step));
    const max = items.reduce((m, q) => Math.max(m, Number(q.step) || 0), 0);
    console.log("[fetchMaxStep] fallback max =>", max);
    return max;
  }
};

const fetchQuestionById = async (id) => {
  const res = await axios.get(`${API_URLS.GET_QUESTION_BY_ID}/${id}`, { headers: headers() });
  return res?.data?.data || null;
};

const createQuestion = async (payload) => {
  const res = await axios.post(API_URLS.CREATE_QUESTION, payload, { headers: headers() });
  return res?.data?.data || null;
};

const updateQuestion = async (id, payload) => {
  const res = await axios.put(`${API_URLS.UPDATE_QUESTION}/${id}`, payload, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

// Update a single option title/sort_order without touching other options or tags
// Uses new backend endpoint: PUT /questions/:id/options/:optionId
const updateSingleOption = async (questionId, optionId, { title, sort_order }) => {
  const res = await axios.put(
    API_URLS.OPTION_UPDATE(questionId, optionId),
    { title, sort_order },
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const deleteQuestion = async (id) => {
  if (id === undefined || id === null || id === "") {
    throw new Error("Missing question id");
  }
  const res = await axios.delete(
    `${API_URLS.DELETE_QUESTION}/${encodeURIComponent(String(id))}`,
    { headers: headers() }
  );
  if (res?.data && res.data.success === false) {
    const err = new Error(res.data?.error?.message || "Delete reported success: false");
    err.response = { status: res.status, data: res.data };
    throw err;
  }
  return res?.data?.data || null;
};

// ── Option Tags and Points ─────────────────────────────────────────────────────

const fetchOptionKeywordPoints = async (questionId, optionId) => {
  const res = await axios.get(API_URLS.OPTION_KP(questionId, optionId), { headers: headers() });
  const payload = res?.data?.data || {};
  return payload.records || [];
};

const createOptionKeywordPoint = async (questionId, optionId, keyword, points) => {
  const res = await axios.post(
    API_URLS.OPTION_KP(questionId, optionId),
    { keyword, points },
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const deleteOptionKeywordPoint = async (questionId, optionId, kpId) => {
  const res = await axios.delete(
    API_URLS.OPTION_KP_BY_ID(questionId, optionId, kpId),
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const updateOptionKeywordPoint = async (questionId, optionId, kpId, { keyword, points }) => {
  const body = {};
  if (keyword !== undefined) body.keyword = keyword;
  if (points !== undefined) body.points = points;
  const res = await axios.put(
    API_URLS.OPTION_KP_BY_ID(questionId, optionId, kpId),
    body,
    { headers: headers() }
  );
  return res?.data?.data || null;
};

// Bulk upsert: send all keywords in one call.
// keywords: [{ keyword, points }]
// Returns the full updated list for the option.
const bulkUpsertOptionKp = async (questionId, optionId, keywords) => {
  const res = await axios.post(
    API_URLS.OPTION_KP_BULK(questionId, optionId),
    { keywords },
    { headers: headers() }
  );
  return res?.data?.data || [];
};

const apiHelpers = {
  fetchQuestions,
  fetchQuestionById,
  fetchMaxStep,
  createQuestion,
  updateQuestion,
  updateSingleOption,
  deleteQuestion,
  fetchOptionKeywordPoints,
  createOptionKeywordPoint,
  deleteOptionKeywordPoint,
  updateOptionKeywordPoint,
  bulkUpsertOptionKp,
  mapError,
  slugify,
};

export default apiHelpers;
